import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { DatePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import { SovosTextField } from 's1-ui';
import { primaryNavy } from 's1-ui/themes/sovos-colors';
import MomentUtils from '@date-io/moment';
import baseTheme from 's1-ui/themes/sovos-enterprise';
import getStateLicenseType from 'data/LicenseTypes/getStateLicenseType';

const styles = {
  dateRow: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'space-between'
  },
  effDate: {
    flex: 1,
    marginRight: '20px',
    '& .sovosDatePicker': {
      width: '100%'
    }
  },
  exDate: {
    flex: 1,
    flexDirection: 'column',
    '& .sovosDatePicker': {
      width: '100%'
    }
  },
  licenseField: {
    marginTop: 20
  },
  underline: {
    '&:not(.bullshit):not(.classes):not(.ftw):after': {
      borderBottom: '2px solid black'
    },
    '&:hover:not(.just):not(.trying):not(.to):not(.win)': {
      '&:before, &:after': { borderBottom: '2px solid black' },
    }
  },
  errorText: {
    color: 'red'
  },
  fullWidth: {
    width: '100%'
  },
  exampleMargin: {
    marginTop: 10
  },
  exampleColor: {
    color: 'gray'
  },
  licenseName: {
    color: 'gray',
    float: 'right'
  }
};

// eslint-disable-next-line arrow-body-style
const getOnChange = (parentValue, parentOnChange, key) => {
  return function onChange(newValue) {
    parentOnChange({ ...parentValue, [key]: newValue });
  };
};

const ShipToState = ({
  value, onChange, errorText, availableLicenses, classes
}) => {
  const sortedLicenses = availableLicenses.sort((a, b) => {
    const nameA = a.RegionName.toUpperCase();
    const nameB = b.RegionName.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
  const errorBool = errorText !== undefined;
  const example = sortedLicenses.filter(license => license.RegionId === value)[0];
  return (
    <div>
      <FormControl className={ classes.fullWidth }>
        <InputLabel htmlFor="shipToState">Ship-to-State</InputLabel>
        <Select
          id="shipToState"
          value={ value }
          onChange={ e => onChange(e.target.value) }
        >
          <MenuItem value={ 0 } key="undefined" disabled>Select a License Type</MenuItem>
          { sortedLicenses.map((license) => {
            const label = (
              <span className={ classes.fullWidth }>
                <span>{license.RegionName}</span>
                <span className={ classes.licenseName }>
                  {license.LicenseName}
                </span>
              </span>
            );
            return <MenuItem value={ license.RegionId } key={ license.RegionName + license.LicenseName }>{label}</MenuItem>;
          })
        }
        </Select>
      </FormControl>
      { example
        && (
        <div className={ classes.exampleMargin }>
          <span className={ classes.exampleColor }>
            ex: {example.FormatExample}
          </span>
        </div>
        )}
      { errorBool
        && (
          <span className={ classes.errorText }>
            { errorText }
          </span>
        )}
    </div>
  );
};

const LicenseField = ({
  underline, value, onChange, errorText, disabled, classes
}) => {
  const errorBool = errorText !== undefined;
  return (
    <div>
      <SovosTextField
        InputProps={ {
          placeholder: 'License Number',
          error: errorBool
        } }
        className={ classes.fullWidth }
        label="License Number"
        value={ value }
        disabled={ disabled }
        onChange={ e => onChange(e.target.value) }
        classes={ { underline } }
      />
      { errorBool
        && (
          <span className={ classes.errorText }>
            { errorText }
          </span>
        )}
    </div>
  );
};

const EffectiveDate = ({
  value, onChange, errorText, classes
}) => {
  const errorBool = errorText !== undefined;
  if (value === undefined) {
    value = new Date();
  }
  return (
    <div>
      <MuiThemeProvider
        theme={
          createMuiTheme({
            ...baseTheme,
            overrides: {
              MuiPickersDay: {
                isSelected: {
                  backgroundColor: primaryNavy
                }
              }
            }
          })
        }
      >
        <MuiPickersUtilsProvider utils={ MomentUtils }>
          <DatePicker
            label="Effective Date"
            value={ value }
            format="MM/DD/YYYY"
            // eslint-disable-next-line no-underscore-dangle
            onChange={ (newValue) => { onChange(new Date(newValue._d)); } }
          />
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
      { errorBool
        && (
          <span className={ classes.errorText }>
            { errorText }
          </span>
        )}
    </div>
  );
};

const ExpirationDate = ({
  value, onChange, errorText, doesNotExpire, classes
}) => {
  const errorBool = errorText !== undefined;
  if (value === undefined) {
    value = new Date();
  }
  return (
    <div>
      <MuiThemeProvider
        theme={
          createMuiTheme({
            ...baseTheme,
            overrides: {
              MuiPickersDay: {
                isSelected: {
                  backgroundColor: primaryNavy
                }
              }
            }
          })
        }
      >
        <MuiPickersUtilsProvider utils={ MomentUtils }>
          <DatePicker
            label="Expiration Date"
            value={ value }
            emptyLabel=""
            disabled={ doesNotExpire }
            format="MM/DD/YYYY"
            // eslint-disable-next-line no-underscore-dangle
            onChange={ (newValue) => { onChange(new Date(newValue._d)); } }
          />
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
      { errorBool
        && (
          <span className={ classes.errorText }>
            { errorText }
          </span>
        )}
    </div>
  );
};

const DoesNotExpire = ({
  value, onChange, errorText, disabled, classes
}) => {
  const errorBool = errorText !== undefined;
  return (
    <div>
      <FormControlLabel
        control={ (
          <Checkbox
            checked={ value }
            onChange={ e => onChange(e.target.checked) }
            disabled={ disabled }
          />
        ) }
        label="Does not expire"
      />
      { errorBool
        && (
          <span className={ classes.errorText }>
            { errorText }
          </span>
        )}
    </div>
  );
};

export const UnstyledLicenseFormDisplay = ({
  classes, value, onChange, errors, availableLicenses
}) => {
  const stateLicenseType = getStateLicenseType(value.RegionId) || {};
  const noLicenseRequired = stateLicenseType.NoLicenseRequired || false;
  return (
    <div>
      <div>
        <ShipToState
          classes={ classes }
          errorText={ errors.RegionId }
          value={ value.RegionId }
          availableLicenses={ availableLicenses }
          onChange={ getOnChange(value, onChange, 'RegionId') }
        />
      </div>
      <div className={ classes.licenseField }>
        <LicenseField
          classes={ classes }
          errorText={ errors.LicenseNumber }
          underline={ classes.underline }
          value={ value.LicenseNumber }
          disabled={ noLicenseRequired }
          onChange={ getOnChange(value, onChange, 'LicenseNumber') }
        />
      </div>
      <div className={ classes.dateRow }>
        <div className={ classes.effDate }>
          <EffectiveDate
            classes={ classes }
            errorText={ errors.EffectiveDate }
            value={ value.EffectiveDate }
            onChange={ getOnChange(value, onChange, 'EffectiveDate') }
          />
        </div>
        <div className={ classes.exDate }>
          <ExpirationDate
            classes={ classes }
            errorText={ errors.ExpirationDate }
            value={ value.ExpirationDate }
            doesNotExpire={ value.DoesNotExpire }
            onChange={ getOnChange(value, onChange, 'ExpirationDate') }
          />
          <DoesNotExpire
            classes={ classes }
            errorText={ errors.EffectiveDate }
            value={ value.DoesNotExpire }
            disabled={ noLicenseRequired }
            onChange={ getOnChange(value, onChange, 'DoesNotExpire') }
          />
        </div>
      </div>
    </div>
  );
};

UnstyledLicenseFormDisplay.propTypes = {
  classes: PropTypes.object.isRequired,
  value: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  availableLicenses: PropTypes.array
};

UnstyledLicenseFormDisplay.defaultProps = {
  availableLicenses: []
};


EffectiveDate.propTypes = {
  value: PropTypes.instanceOf(Date),
  errorText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
};

EffectiveDate.defaultProps = {
  value: undefined,
  errorText: undefined
};

ExpirationDate.propTypes = {
  value: PropTypes.instanceOf(Date),
  errorText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  doesNotExpire: PropTypes.bool,
  classes: PropTypes.object.isRequired
};

ExpirationDate.defaultProps = {
  value: undefined,
  errorText: undefined,
  doesNotExpire: false
};

ShipToState.propTypes = {
  value: PropTypes.number,
  errorText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  availableLicenses: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired
};

ShipToState.defaultProps = {
  value: 0,
  errorText: undefined
};

DoesNotExpire.propTypes = {
  value: PropTypes.bool,
  errorText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired
};

DoesNotExpire.defaultProps = {
  value: false,
  errorText: undefined,
  disabled: false
};

LicenseField.propTypes = {
  value: PropTypes.string,
  errorText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  underline: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired
};

LicenseField.defaultProps = {
  value: '',
  errorText: undefined,
  disabled: false
};

export default withStyles(styles)(UnstyledLicenseFormDisplay);
